import React from 'react';
import { Link } from 'react-router-dom';
import { TEAM_BY_ID, TEAM_DEPARTMENTS, TeamMember } from '../data/site';
import { track, EVENTS } from '../utils/analytics';
import '../styles/departments.css';

/** A 28px portrait chip; a neutral initial tile when no approved portrait exists. */
export const MemberChip: React.FC<{ m: TeamMember; size?: number }> = ({ m, size = 28 }) => (
  <span
    className="chip"
    style={{ ['--tc' as string]: m.accent, width: size, height: size }}
    title={`${m.name}, ${m.role.toLowerCase()}`}
  >
    {m.art ? (
      <img src={m.art} alt="" width={size} height={size} loading="lazy" decoding="async" />
    ) : (
      <b aria-hidden="true">{m.name[0]}</b>
    )}
  </span>
);

/**
 * Home: three departments in one row, one line each, and a link to the team
 * page. The full roster used to live here; it reads better on /team where it
 * has room, so the home page only says what the departments do.
 */
const DepartmentsStrip: React.FC = () => (
  <section id="departments" className="band" aria-labelledby="departments-title">
    <div className="shell">
      <div className="lede-row">
        <h2 className="h2" id="departments-title">Three departments. You pick what you need.</h2>
        <p>Each one is run by Arceus and its specialists, with JK people reviewing the work. You approve anything that spends, posts or texts.</p>
      </div>

      <ol className="depts">
        {TEAM_DEPARTMENTS.map((d, i) => {
          const members = d.members.map((id) => TEAM_BY_ID[id]).filter(Boolean);
          return (
            <li className="dept" key={d.id}>
              <span className="dept__num">0{i + 1}</span>
              <h3 className="dept__name">{d.name}</h3>
              <p className="dept__body">{d.body}</p>
              <ul className="dept__examples">
                {d.examples.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
              <p className="dept__who">
                <span className="dept__chips" aria-hidden="true">
                  {members.map((m) => (
                    <MemberChip m={m} key={m.id} />
                  ))}
                </span>
                <span>{members.map((m) => m.name).join(', ')}</span>
              </p>
            </li>
          );
        })}
      </ol>

      <p className="depts__more">
        <Link
          className="link"
          to="/team"
          onClick={() => track(EVENTS.ctaClicked, { location: 'home-departments', label: 'Meet the team' })}
        >
          Meet the team
          <span className="link__arrow" aria-hidden="true">→</span>
        </Link>
      </p>
    </div>
  </section>
);

export default React.memo(DepartmentsStrip);
